import { HeadingLarge, Tabs, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { FC } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Loading } from '../../components';
import { PageWrapper } from '../../components/Common';
import { NoSurveysInfo } from '../../components/NoSurveysInfo';
import {
  Features,
  OrganizationQuery,
  OrganizationSurveysQuery,
  SurveyStatus,
  useOrganizationSurveysQuery,
} from '../../generated/hooks';
import { useFeatureFlag } from '../../lib/helpers/hooks';
import { isPulserSurvey } from '../../lib/helpers/pulser';
import { SurveyTable } from './SurveyTable';
import { PATHS } from './types';

const ButtonContainer = styled(Button)`
  margin-top: 0;
  margin-bottom: ${theme.spacing.f};
`;

type OrganizationSurvey = NonNullable<OrganizationSurveysQuery['organizationSurveys']>[0];

interface IProps {
  organization: NonNullable<OrganizationQuery['organization']>;
  heading?: string;
  headingIcom?: string;
  surveysBasePath?: string;
  schedulePath?: string;
  filterSurveys?: (survey: OrganizationSurvey) => boolean;
}

export const SurveysScreen: FC<IProps> = ({
  organization,
  heading,
  headingIcom = 'heading-standalone-surveys',
  surveysBasePath = '/surveys',
  schedulePath = '/conversationSubjects',
  filterSurveys,
}) => {
  const [t, i18n] = useTranslation('surveysOverview');
  const { status } = useParams<{ status?: string }>();
  const history = useHistory();
  const hasPulserModule = useFeatureFlag(Features.PulserModule);

  const { data, loading, error } = useOrganizationSurveysQuery({
    variables: { languageCode: i18n.language, organizationId: organization.id },
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <></>;
  }

  const scopedSurveys = (data?.organizationSurveys ?? []).filter((survey) => {
    if (filterSurveys) {
      return filterSurveys(survey);
    }
    if (hasPulserModule && isPulserSurvey(survey)) {
      return false;
    }
    return true;
  });

  const surveys = scopedSurveys.filter((s) => {
    if ((!status || status === PATHS.ONGOING) && s.status === SurveyStatus.Activating) {
      // Show activating surveys in ongoing tab as well
      return true;
    }
    if (status === PATHS.PREPARED && s.status === SurveyStatus.Notstarted) {
      return true;
    }
    return s.status.toLowerCase() === (status || PATHS.ONGOING);
  });

  const isWithoutSurveys = scopedSurveys.length === 0;

  return (
    <PageWrapper data-cy="admin-page-content">
      <HeadingLarge data-icom={headingIcom}>{heading ?? t('standaloneSurveys')}</HeadingLarge>
      <ButtonContainer
        onClick={() => history.push(schedulePath)}
        data-icom={'btn-create-new-survey'}
        data-cy="btn-create-new-survey"
      >
        {t('newSurveyFromTemplate')}
      </ButtonContainer>

      <Tabs
        items={Object.values(PATHS).map((s) => ({
          title: t(s),
          link: `${surveysBasePath}/${s}`,
          active: (!status && s === PATHS.ONGOING) || s === status,
          icomTag: `tab-surveys-${s}`,
        }))}
        theme={theme}
      />
      {surveys && surveys.length > 0 ? (
        <SurveyTable organization={organization} surveys={surveys} pathStatus={status} />
      ) : (
        <NoSurveysInfo
          status={status ? (status as PATHS) : PATHS.ONGOING}
          isWithoutSurveys={isWithoutSurveys}
          schedulePath={schedulePath}
          dataCy={isWithoutSurveys ? 'admin-before-survey' : undefined}
        />
      )}
    </PageWrapper>
  );
};
